/**
 * DtOsLogin — DT OS employee login screen.
 *
 * Shown when:
 *   - window.shopify is unavailable (non-Shopify context), OR
 *   - The Shopify user is not the store owner
 *
 * On success: stores the DT OS session token in memory (never localStorage)
 * and calls onLogin() so the parent re-renders with the employee session.
 */

import { useState }      from 'react'
import {
  Banner, BlockStack, Button, Card,
  FormLayout, Page, Text, TextField,
} from '@shopify/polaris'
import { ENV }              from '../config/environment'
import { getShop }          from '../sdk/shop'
import { storeDtOsToken }   from '../hooks/useAppSession'
import type { EmployeeSession } from '../hooks/useAppSession'

type Props = {
  onLogin: (session: EmployeeSession) => void
}

export function DtOsLogin({ onLogin }: Props) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [busy,     setBusy]     = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  async function handleLogin() {
    const shop = getShop()
    if (!shop || !username.trim() || !password) return

    setBusy(true)
    setError(null)

    try {
      const url = new URL(`${ENV.API_BASE_URL}/auth/login`)
      url.searchParams.set('shop', shop)

      const res = await fetch(url.toString(), {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username: username.trim(), password }),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as { error: string }
        if (res.status === 401)  setError('Incorrect username or password.')
        else if (res.status === 403) setError('This account is inactive — contact your administrator.')
        else setError(body.error ?? 'Unable to sign in — please try again.')
        return
      }

      // loginEmployee returns the full LoginResult (token + memberships)
      const data = await res.json() as {
        token:          string
        employeeId:     string
        displayName:    string
        tenantRole:     'admin' | 'member'
        mustChangePass: boolean
        memberships:    EmployeeSession['memberships']
        opsPermissions: Record<string, unknown> | null
      }

      storeDtOsToken(data.token)
      onLogin({
        type:           'employee',
        employeeId:     data.employeeId,
        displayName:    data.displayName,
        tenantRole:     data.tenantRole,
        mustChangePass: data.mustChangePass,
        memberships:    data.memberships,
        opsPermissions: data.opsPermissions ?? null,
      })
    } catch {
      setError('Unable to connect — please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page narrowWidth title="DT OS — Sign in">
      <Card>
        <BlockStack gap="400">
          <Text as="p" tone="subdued">
            Enter your DT OS credentials to continue.
          </Text>
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}
          <FormLayout>
            <TextField
              label="Username"
              value={username}
              onChange={setUsername}
              autoComplete="username"
              requiredIndicator
            />
            <div
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' && !busy) handleLogin()
              }}
            >
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                requiredIndicator
              />
            </div>
          </FormLayout>
          <Button
            variant="primary"
            onClick={handleLogin}
            loading={busy}
            disabled={!username.trim() || !password}
          >
            Sign in
          </Button>
        </BlockStack>
      </Card>
    </Page>
  )
}
